/**
 * Sync Service - Handles synchronization of offline data when connection returns
 */
import api from '../lib/api';
import {
  getPendingSyncItems,
  updateSyncItemStatus,
  removeSyncedItem,
  removeSyncedSale,
  removeSyncedOrder,
  getOfflineStats,
} from './offlineDB';

const MAX_SYNC_ATTEMPTS = 3;
const SYNC_RETRY_DELAY = 5000; // 5 seconds

let isSyncing = false;
let syncListeners = [];

/**
 * Register a listener for sync events
 */
export const onSyncEvent = (callback) => {
  syncListeners.push(callback);
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback);
  };
};

/**
 * Emit sync event to all listeners
 */
const emitSyncEvent = (event) => {
  syncListeners.forEach(callback => {
    try {
      callback(event);
    } catch (e) {
      console.error('Sync listener error:', e);
    }
  });
};

/**
 * Sync a single sale to the server
 */
const syncSale = async (saleData) => {
  // Remove offline-specific fields
  const { localId, syncAttempts, status: localStatus, ...sale } = saleData;
  
  const response = await api.post('/sales', sale);
  return response.data;
};

/**
 * Sync a single order to the server
 */
const syncOrder = async (orderData) => {
  const { localId, syncAttempts, status: localStatus, ...order } = orderData;
  
  // Check if this is a new order or an update
  if (order.serverId) {
    // Update existing order
    const response = await api.put(`/orders/${order.serverId}`, order);
    return response.data;
  } else {
    // Create new order
    const response = await api.post('/orders', order);
    return response.data;
  }
};

/**
 * Process a single sync queue item
 */
const processSyncItem = async (item) => {
  try {
    switch (item.type) {
      case 'sale':
        await syncSale(item.data);
        await removeSyncedSale(item.data.localId);
        break;
        
      case 'order':
        await syncOrder(item.data);
        await removeSyncedOrder(item.data.localId);
        break;
        
      case 'order_update':
        await syncOrder(item.data);
        break;
        
      default:
        console.warn('Unknown sync item type:', item.type);
        return false;
    }
    
    // Remove from sync queue on success
    await removeSyncedItem(item.id);
    return true;
  } catch (error) {
    console.error('Sync error for item:', item.id, error);
    
    // Update attempt count and status
    if (item.attempts >= MAX_SYNC_ATTEMPTS - 1) {
      await updateSyncItemStatus(item.id, 'failed', error.message);
    } else {
      await updateSyncItemStatus(item.id, 'pending', error.message);
    }
    
    return false;
  }
};

/**
 * Main sync function - processes all pending items
 */
export const syncPendingData = async () => {
  if (isSyncing) {
    console.log('Sync already in progress');
    return { synced: 0, failed: 0, remaining: 0 };
  }
  
  // Check if online
  if (!navigator.onLine) {
    console.log('Cannot sync - offline');
    return { synced: 0, failed: 0, remaining: 0 };
  }
  
  isSyncing = true;
  emitSyncEvent({ type: 'sync_started' });
  
  let synced = 0;
  let failed = 0;
  
  try {
    const pendingItems = await getPendingSyncItems();
    
    if (pendingItems.length === 0) {
      emitSyncEvent({ type: 'sync_complete', synced: 0, failed: 0 });
      return { synced: 0, failed: 0, remaining: 0 };
    }
    
    emitSyncEvent({ 
      type: 'sync_progress', 
      total: pendingItems.length, 
      current: 0 
    });
    
    for (let i = 0; i < pendingItems.length; i++) {
      const item = pendingItems[i];
      
      // Only process items that haven't exceeded max attempts
      if (item.attempts < MAX_SYNC_ATTEMPTS) {
        const success = await processSyncItem(item);
        
        if (success) {
          synced++;
        } else {
          failed++;
        }
        
        emitSyncEvent({ 
          type: 'sync_progress', 
          total: pendingItems.length, 
          current: i + 1,
          synced,
          failed
        });
      }
    }
    
    const stats = await getOfflineStats();
    
    emitSyncEvent({ 
      type: 'sync_complete', 
      synced, 
      failed, 
      remaining: stats.pendingSync 
    });
    
    return { synced, failed, remaining: stats.pendingSync };
    
  } catch (error) {
    console.error('Sync process error:', error);
    emitSyncEvent({ type: 'sync_error', error: error.message });
    return { synced, failed, remaining: -1 };
    
  } finally {
    isSyncing = false;
  }
};

/**
 * Auto-sync when online
 */
let autoSyncTimeout = null;

export const startAutoSync = () => {
  // Listen for online event
  window.addEventListener('online', handleOnline);
  
  // If already online, try to sync
  if (navigator.onLine) {
    scheduleSync();
  }
};

export const stopAutoSync = () => {
  window.removeEventListener('online', handleOnline);
  if (autoSyncTimeout) {
    clearTimeout(autoSyncTimeout);
    autoSyncTimeout = null;
  }
};

const handleOnline = () => {
  console.log('Connection restored - starting sync');
  emitSyncEvent({ type: 'connection_restored' });
  scheduleSync();
};

const scheduleSync = (delay = 1000) => {
  if (autoSyncTimeout) {
    clearTimeout(autoSyncTimeout);
  }
  
  autoSyncTimeout = setTimeout(async () => {
    const result = await syncPendingData();
    
    // If there are still items to sync and we're online, retry
    if (result.remaining > 0 && navigator.onLine) {
      scheduleSync(SYNC_RETRY_DELAY);
    }
  }, delay);
};

/**
 * Force sync now
 */
export const forceSync = () => {
  return syncPendingData();
};

/**
 * Get sync status
 */
export const getSyncStatus = () => {
  return {
    isSyncing,
    isOnline: navigator.onLine,
  };
};
